import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLink, getPageLinkByNodeId, LinkModel, mapLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion, { AccordionProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Button from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Minus from "@insite/mobius/Icons/Minus";
import Link from "@insite/mobius/Link";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { HasHistory, History, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Plus from "@tIntermatic/Icons/Plus";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    label = "label",
    link = "link",
    buttons = "buttons",
    discontinuedProductPageLink = "discontinuedProductPageLink",
}

interface WhereToBuyLinkModel {
    fields: {
        openInNewWindow: boolean;
        overriddenTitle: string;
        locationType?: string;
        destination: LinkFieldValue;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.label]: string;
        [fields.link]: LinkFieldValue;
        [fields.buttons]: WhereToBuyLinkModel[];
        [fields.discontinuedProductPageLink]: LinkFieldValue;
    };
}

export interface IntWhereToBuyStyles {
    wrapper?: InjectableCss;
    toggleIconProps?: IconPresentationProps;
    specificationsAccordionSection?: AccordionSectionPresentationProps;
    buttonWrapper?: InjectableCss;
    buttonStyles?: InjectableCss;
    discontinuedCss?: InjectableCss;
}

export const intWhereToBuyStyles: IntWhereToBuyStyles = {
    wrapper: {
        css: css`
            border-top: 1px solid rgba(0, 0, 0, 0.1);
        `,
    },
    specificationsAccordionSection: {
        toggleIconProps: {
            src: Minus,
            size: 15,
        },

        expandIconProps: {
            src: Plus,
            size: 15,
        },

        headerProps: {
            css: css`
                > button {
                    background: #fff;
                    border: none;
                    padding: 13px 9px 13px 15px !important;
                    > span {
                        font-size: 18px;
                        font-weight: 600;
                        letter-spacing: 0;
                        line-height: 22px;
                        color: #333333;
                        text-transform: capitalize;
                    }
                }
            `,
        },

        panelProps: {
            css: css`
                border: none;
                padding-top: 0px;
                font-size: 16px;
                font-weight: 300;
                letter-spacing: 0;
                line-height: 24px;
                color: #33333a;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding-bottom: 23px;
                            padding-right: 22px;
                        `,
                        css`
                            padding-bottom: 23px;
                            padding-right: 22px;
                        `,
                        css`
                            padding-bottom: 23px;
                            padding-right: 22px;
                        `,
                        css`
                            padding-bottom: 33px;
                        `,
                        css`
                            padding-bottom: 33px;
                        `,
                    ])}
            `,
        },
    },

    buttonStyles: {
        css: css`
            width: 100%;
            padding: 0 18px;
            span[class^="TypographyStyle"] {
                font-size: 12px;
                font-weight: 600;
                letter-spacing: 2px;
                text-align: center;
                color: #ffffff;
                text-transform: uppercase;
                &:hover {
                    background: #000000 !important;
                    border-color: #000000 !important;
                }
            }
            &:hover {
                background: #000000 !important;
                border-color: #000000 !important;
            }
        `,
    },

    buttonWrapper: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        margin-top: 10px;
                    `,
                    css`
                        width: 100%;
                        margin-top: 10px;
                    `,
                    css`
                        width: 100%;
                        margin-top: 10px;
                    `,
                    css`
                        width: 180px;
                        display: inline-block;
                        margin-right: 9px;
                        margin-top: 15px;
                        vertical-align: middle;
                    `,
                    css`
                        width: 180px;
                        display: inline-block;
                        margin-right: 9px;
                        margin-top: 15px;
                        vertical-align: middle;
                    `,
                ])}
        `,
    },

    discontinuedCss: {
        css: css`
            font-size: 16px;
            font-weight: 300;
            letter-spacing: 0;
            line-height: 23px;
            color: #33333a;
        `,
    },
};

const styles = intWhereToBuyStyles;

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const buttons = mapLinks<
        WhereToBuyLinkModel,
        { openInNewWindow: boolean; overriddenTitle: string; locationType?: string }
    >(state, ownProps.fields.buttons, widgetLink => ({
        openInNewWindow: widgetLink.fields.openInNewWindow,
        overriddenTitle: widgetLink.fields.overriddenTitle,
        locationType: widgetLink.fields.locationType,
    }));
    return {
        buttons,
        state,
        currentLanguage: state.context.session.language,
        currentWebsiteName: state.context.website.name,
    };
};

const onClick = (url: string | undefined, locationType?: string, openInNewWindow?: boolean) => {
    if (url) {
        let filterUrl = url;
        if (locationType) {
            filterUrl = `${url}?locationType=${locationType}`;
        }
        if (openInNewWindow) {
            window.open(filterUrl);
        } else {
            window.location.href = filterUrl;
        }
    }
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProduct;

const INTWhereToBuy = ({ fields, buttons, product, state, currentLanguage, currentWebsiteName }: Props) => {
    if (!product) {
        return null;
    }

    if (product?.properties?.isFamilySeries === "True") {
        return null;
    }
    const discontinuedPagelink = getPageLinkByNodeId(state, fields?.discontinuedProductPageLink?.value);
    const canDisplay =
        currentWebsiteName.toLowerCase() === "main" && currentLanguage?.languageCode.toLowerCase() === "en";
    return (
        <Accordion headingLevel={1} {...styles.wrapper}>
            <ManagedAccordionSection
                title={fields?.title}
                initialExpanded={true}
                {...styles.specificationsAccordionSection}
            >
                {product.properties?.isObsolete?.toLowerCase() === "true" || (canDisplay && product.cantBuy) ? (
                    <StyledWrapper>
                        {product.properties?.isObsolete?.toLowerCase() === "true" && (
                            <StyledWrapper>
                                <StyledWrapper
                                    css={css`
                                        color: #b31515;
                                        font-weight: 700;
                                    `}
                                >
                                    {siteMessage("Discontinued_ProductMeassge")}
                                </StyledWrapper>
                                {siteMessage("DiscontinuedMessage_ProductLink")}
                            </StyledWrapper>
                        )}
                        {canDisplay && product.cantBuy && (
                            <StyledWrapper
                                css={css`
                                    color: #b31515;
                                    font-weight: 700;
                                `}
                            >
                                {siteMessage("Product_Non_Purchasable")}
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                ) : (
                    buttons.map((button, index) => (
                        <StyledWrapper
                            // eslint-disable-next-line react/no-array-index-key
                            key={index}
                            {...styles.buttonWrapper}
                        >
                            <Button
                                {...styles.buttonStyles}
                                onClick={() => onClick(button?.url, button?.locationType, button?.openInNewWindow)}
                            >
                                {button?.overriddenTitle}
                            </Button>
                        </StyledWrapper>
                    ))
                )}
            </ManagedAccordionSection>
        </Accordion>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProduct(INTWhereToBuy)),
    definition: {
        group: "Basic",
        icon: "Button",
        displayName: "INTWhereToBuy",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Where to buy",
                fieldType: "Translatable",
                sortOrder: 0,
                isRequired: true,
            },
            {
                name: fields.discontinuedProductPageLink,
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                isRequired: true,
                fieldType: "General",
            },
            {
                name: fields.buttons,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        return value; // TODO ISC-10781 make this work
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "locationType",
                        displayName: "Location Type",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
